import api from './api'

// Ids are typed `number | string` and are interpolated into request URLs, so
// they are encoded first: a value containing '/', '?' or '../' would otherwise
// rewrite the path and hit an unintended endpoint. No-op for numeric ids.
const segment = (id: number | string): string => encodeURIComponent(String(id))

export const paymentsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/payments', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/payments/${segment(id)}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/payments', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/payments/${segment(id)}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/payments/${segment(id)}`),
  getPayments: (params: Record<string, any> = {}): Promise<any> => api.get('/payments', { params }),
  getPendingDrafts: (params: Record<string, any> = {}): Promise<any> => api.get('/draft-payments', { params }),
  finalize: (id: number | string): Promise<any> => api.post(`/payments/${segment(id)}/finalize`),
  finalizeBatch: (ids: (number | string)[]): Promise<any> => api.post('/payments/finalize-batch', { ids }),
}

// The per-type payment_sale/payment_purchase/... routes are commented out on
// the backend. Mutations go through the universal /payments endpoints; the
// per-document list endpoints below are the real module routes.
export const salePaymentsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/payments', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/payments/${segment(id)}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/payments', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/payments/${segment(id)}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/payments/${segment(id)}`),
  getBySaleId: (saleId: number | string): Promise<any> => api.get(`get_payments_by_sale/${segment(saleId)}`),
}

export const purchasePaymentsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/payments', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/payments/${segment(id)}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/payments', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/payments/${segment(id)}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/payments/${segment(id)}`),
  getByPurchaseId: (purchaseId: number | string): Promise<any> => api.get(`get_payments_by_purchase/${segment(purchaseId)}`),
}

export const saleReturnPaymentsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/payments', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/payments/${segment(id)}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/payments', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/payments/${segment(id)}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/payments/${segment(id)}`),
  getByReturnId: (returnId: number | string): Promise<any> => api.get(`returns/sale/payment/${segment(returnId)}`),
}

export const purchaseReturnPaymentsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/payments', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/payments/${segment(id)}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/payments', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/payments/${segment(id)}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/payments/${segment(id)}`),
  getByReturnId: (returnId: number | string): Promise<any> => api.get(`returns/purchase/payment/${segment(returnId)}`),
}

// Namespaced, NOT spread: all five groups declare the same getAll/getById/
// create/update/delete (and the two return groups also collide on
// getByReturnId), so a flat merge silently let the last spread win and hid
// every route divergence. Import the groups directly instead.
export const paymentsCombinedApi = {
  payments: paymentsApi,
  salePayments: salePaymentsApi,
  purchasePayments: purchasePaymentsApi,
  saleReturnPayments: saleReturnPaymentsApi,
  purchaseReturnPayments: purchaseReturnPaymentsApi,
}

export const draftJournalsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/draft-journals', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/draft-journals/${segment(id)}`),
  finalize: (id: number | string): Promise<any> => api.post(`/draft-journals/${segment(id)}/finalize`),
  finalizeAll: (): Promise<any> => api.post('/draft-journals/finalize-all'),
}

// Draft payment finalization lives on the universal /payments routes (see
// paymentsApi.finalize / finalizeBatch) — /draft-payments only exposes the
// pending-drafts listing.
export const draftPaymentsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/draft-payments', { params }),
}

export default paymentsApi
